import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";

const secondaryClassName = [
	"wi-link-draw inline-flex min-h-11 items-center gap-1 font-sans text-body-sm font-medium text-wi-gold",
	"focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-wi-gold",
	"disabled:cursor-not-allowed disabled:opacity-40",
].join(" ");

type Common = {
	children: ReactNode;
	className?: string;
	/** Append a → affordance (default true). */
	arrow?: boolean;
};

type AsRouterLink = Common &
	Omit<LinkProps, "className" | "children"> & {
		to: LinkProps["to"];
		href?: never;
	};

type AsAnchor = Common &
	Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className" | "children"> & {
		href: string;
		to?: never;
	};

type AsButton = Common &
	Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "children" | "type"> & {
		to?: never;
		href?: never;
		type?: "button" | "submit" | "reset";
	};

export type SecondaryLinkProps = AsRouterLink | AsAnchor | AsButton;

function withArrow(children: ReactNode, arrow: boolean): ReactNode {
	if (!arrow) return children;
	return (
		<>
			{children}
			<span aria-hidden="true">→</span>
		</>
	);
}

/**
 * Secondary CTA — `wi-gold` text link (underline on hover + optional arrow).
 * Place on the **same row** as {@link PrimaryButton} (`.wi-cta-row`); never an equal-weight stacked button.
 */
export function SecondaryLink(props: SecondaryLinkProps) {
	const classes = [secondaryClassName, props.className ?? ""].filter(Boolean).join(" ");
	const arrow = props.arrow !== false;
	const label = withArrow(props.children, arrow);

	if ("to" in props && props.to != null) {
		const { to, className: _className, arrow: _arrow, children: _children, ...rest } = props;
		return (
			<Link to={to} className={classes} {...rest}>
				{label}
			</Link>
		);
	}

	if ("href" in props && typeof props.href === "string") {
		const { href, className: _className, arrow: _arrow, children: _children, ...rest } = props;
		return (
			<a href={href} className={classes} {...rest}>
				{label}
			</a>
		);
	}

	const buttonProps = props as AsButton;
	const { className: _className, arrow: _arrow, children: _children, type = "button", ...rest } = buttonProps;
	return (
		<button type={type} className={classes} {...rest}>
			{label}
		</button>
	);
}
